import {
  addPlanningInThePastOverrideRequest,
  getPlanningInThePastEligibleApiUsersRequest,
  getPlanningInThePastOverrideRequest,
  getPlanningInThePastRoleDefaultRequest,
  parseContractComplianceError,
  removePlanningInThePastOverrideRequest,
  savePlanningInThePastRoleDefaultRequest,
  updatePlanningInThePastOverrideRequest,
} from 'api/requests/contract-compliance-requests';
import { CoefficientsPerWorkerClass } from 'constants/CoefficientsPerWorkerclass';
import { PlanningInThePastRole } from 'constants/PlanningInThePastRole';
import { PlanningInThePastState } from 'constants/PlanningInThePastState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__CONCURRENT_CHANGE,
  CONTRACT_COMPLIANCE__NOT_FOUND,
  CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR,
  CONTRACT_COMPLIANCE__PLANNING_OVERRIDE_REMOVED,
  CONTRACT_COMPLIANCE__PLANNING_OVERRIDE_SAVED,
  CONTRACT_COMPLIANCE__PLANNING_ROLE_DEFAULT_SAVED,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import {
  IPlanningInThePastEligibleApiUser,
  IPlanningInThePastEntry,
  IRemoveRuleException,
  ParsedContractComplianceError,
} from 'types/contract-compliance/IContractCompliance';
// The panel contract (callbacks and statuses) is shared by every side panel of the domain.
import { PanelCallbacks, PanelStatus } from '../../hooks/types';
import { useJustification } from '../../hooks/useJustification';
import { getContractComplianceErrorMessage } from '../../utils';
import { buildStates, toWorkerClassStates, WorkerClassStates } from '../utils';

/** `role`: edit a Role default. `add` / `edit`: a new / an existing per-user override. */
export type PlanningPanelMode = 'role' | 'add' | 'edit';

export interface PlanningPanelCallbacks extends PanelCallbacks {
  /** Removing failed after the disclaimer was confirmed; the toast offers `retry`. */
  onRemoveFailed: (message: string, retry: () => void) => void;
}

/**
 * Side panel of the "Planning in the past" section: edits a Role default, or adds / edits / removes the override of
 * an API user. Same lifecycle as the break hours panels (side panel pattern): the section reloads on save and close.
 */
export const usePlanningInThePastPanel = (callbacks: PlanningPanelCallbacks) => {
  const [t] = useTranslation();
  const justification = useJustification();
  const [mode, setMode] = useState<PlanningPanelMode>('role');
  const [role, setRole] = useState<PlanningInThePastRole | null>(null);
  const [status, setStatus] = useState<PanelStatus>('closed');
  const [entry, setEntry] = useState<IPlanningInThePastEntry | null>(null);
  const [eligibleUsers, setEligibleUsers] = useState<IPlanningInThePastEligibleApiUser[]>([]);
  const [states, setStates] = useState<WorkerClassStates>(() => buildStates(null));
  const [selectedApiUserId, setSelectedApiUserId] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const savingRef = useRef(false);
  // Bumped on every open and close, so answers for a panel the user has left are dropped.
  const sessionRef = useRef(0);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const { reset: resetJustification, toPayload } = justification;

  // A save error stays until the user's first edit after it (reloads after a conflict do not count).
  const { text: justificationText, document: justificationDocument } = justification;
  useEffect(() => {
    setSaveErrorMessage(null);
  }, [justificationText, justificationDocument]);

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setRole(null);
    setStatus('closed');
    setEntry(null);
    setEligibleUsers([]);
    setStates(buildStates(null));
    setSelectedApiUserId('');
    setSelectedScopes([]);
    setIsConfirmOpen(false);
    setIsRemoveOpen(false);
    setLoadErrorMessage(null);
    setSaveErrorMessage(null);
    resetJustification();
  }, [resetJustification]);

  /** Shared by every load: a 404 closes the panel; anything else leaves the panel open with a retry. */
  const handleLoadError = useCallback(
    (error: unknown, session: number) => {
      if (session !== sessionRef.current) return;
      const parsed = parseContractComplianceError(error);
      const message = getContractComplianceErrorMessage(t, parsed, CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR);
      if (parsed.key === CONTRACT_COMPLIANCE__NOT_FOUND) {
        reset();
        callbacksRef.current.onGone(message);
        return;
      }
      setLoadErrorMessage(message);
      setStatus('loadError');
    },
    [reset, t],
  );

  const loadRole = useCallback(
    async (target: PlanningInThePastRole, session: number) => {
      setStatus('loading');
      setLoadErrorMessage(null);
      try {
        const value = await getPlanningInThePastRoleDefaultRequest(target);
        if (session !== sessionRef.current) return;
        setEntry(value);
        setStates(buildStates(value));
        setStatus('ready');
      } catch (error) {
        handleLoadError(error, session);
      }
    },
    [handleLoadError],
  );

  const loadOverride = useCallback(
    async (id: number, session: number) => {
      setStatus('loading');
      setLoadErrorMessage(null);
      try {
        const value = await getPlanningInThePastOverrideRequest(id);
        if (session !== sessionRef.current) return;
        setEntry(value);
        setStates(buildStates(value));
        setSelectedScopes(value.apiScopes);
        setStatus('ready');
      } catch (error) {
        handleLoadError(error, session);
      }
    },
    [handleLoadError],
  );

  const loadEligibleUsers = useCallback(
    async (session: number) => {
      setStatus('loading');
      setLoadErrorMessage(null);
      try {
        const users = await getPlanningInThePastEligibleApiUsersRequest();
        if (session !== sessionRef.current) return;
        setEligibleUsers(users);
        setStatus('ready');
      } catch (error) {
        handleLoadError(error, session);
      }
    },
    [handleLoadError],
  );

  const openRole = useCallback(
    (target: PlanningInThePastRole) => {
      reset();
      setMode('role');
      setRole(target);
      loadRole(target, sessionRef.current);
    },
    [loadRole, reset],
  );

  const openAdd = useCallback(() => {
    reset();
    setMode('add');
    loadEligibleUsers(sessionRef.current);
  }, [loadEligibleUsers, reset]);

  const openEdit = useCallback(
    (row: IPlanningInThePastEntry) => {
      reset();
      setMode('edit');
      setEntry(row);
      loadOverride(row.id, sessionRef.current);
    },
    [loadOverride, reset],
  );

  const retryLoad = useCallback(() => {
    const session = sessionRef.current;
    if (mode === 'role' && role) loadRole(role, session);
    else if (mode === 'add') loadEligibleUsers(session);
    else if (mode === 'edit' && entry) loadOverride(entry.id, session);
  }, [mode, role, entry, loadRole, loadEligibleUsers, loadOverride]);

  const setWorkerClassState = useCallback((workerClass: CoefficientsPerWorkerClass, state: PlanningInThePastState) => {
    setStates(previous => ({ ...previous, [workerClass]: state }));
    setSaveErrorMessage(null);
  }, []);

  /** An override usually covers every API the user calls, so all of them start selected. */
  const selectApiUser = useCallback(
    (id: string) => {
      setSelectedApiUserId(id);
      setSelectedScopes(eligibleUsers.find(user => String(user.id) === id)?.apiScopes ?? []);
      setSaveErrorMessage(null);
    },
    [eligibleUsers],
  );

  const selectScopes = useCallback((scopes: string[]) => {
    setSelectedScopes(scopes);
    setSaveErrorMessage(null);
  }, []);

  const selectedUser = eligibleUsers.find(user => String(user.id) === selectedApiUserId) ?? null;
  const availableScopes = ((): string[] => {
    if (mode === 'add') return selectedUser?.apiScopes ?? [];
    if (mode === 'edit') return entry?.availableApiScopes ?? [];
    return [];
  })();

  const isComplete = mode === 'role' || (selectedScopes.length > 0 && (mode === 'edit' || selectedApiUserId !== ''));

  // Save always asks for confirmation and always sends the request, even when nothing was changed: skipping both made
  // the click look like a plain refresh. The API treats an unchanged save as a no-op (no write, no audit entry).
  const requestSave = useCallback(() => {
    if (!isComplete || status !== 'ready' || savingRef.current || justification.isUploading) return;
    setIsConfirmOpen(true);
  }, [isComplete, status, justification.isUploading]);

  const cancelConfirm = useCallback(() => setIsConfirmOpen(false), []);

  /** Shared failure handling for save and remove. Returns false when the error is left to the caller. */
  const handleWriteError = useCallback(
    (
      parsed: ParsedContractComplianceError,
      message: string,
      session: number,
      reloadEntry: (() => void) | null,
    ): boolean => {
      const stillShowing = session === sessionRef.current;
      if (parsed.key === CONTRACT_COMPLIANCE__NOT_FOUND) {
        if (stillShowing) reset();
        callbacksRef.current.onGone(message);
        return true;
      }
      if (!stillShowing) {
        if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE) callbacksRef.current.onStale();
        callbacksRef.current.onError(message);
        return true;
      }
      if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE && reloadEntry) {
        // Reload so the user sees the other change and the next save carries the fresh rowVersion.
        setSaveErrorMessage(message);
        callbacksRef.current.onStale();
        reloadEntry();
        return true;
      }
      return false;
    },
    [reset],
  );

  const confirmSave = useCallback(async () => {
    if (savingRef.current) return;
    const session = sessionRef.current;
    const workerClassStates = toWorkerClassStates(states);
    savingRef.current = true;
    setIsSaving(true);
    setIsConfirmOpen(false);
    setSaveErrorMessage(null);
    let reloadEntry: (() => void) | null = null;
    try {
      let savedMessageKey = CONTRACT_COMPLIANCE__PLANNING_OVERRIDE_SAVED;
      if (mode === 'role' && role) {
        reloadEntry = () => loadRole(role, session);
        savedMessageKey = CONTRACT_COMPLIANCE__PLANNING_ROLE_DEFAULT_SAVED;
        await savePlanningInThePastRoleDefaultRequest(role, {
          rowVersion: entry?.rowVersion ?? null,
          workerClassStates,
          ...toPayload(),
        });
      } else if (mode === 'add') {
        await addPlanningInThePastOverrideRequest({
          apiUserId: Number(selectedApiUserId),
          apiScopes: selectedScopes,
          workerClassStates,
          ...toPayload(),
        });
      } else if (mode === 'edit' && entry) {
        reloadEntry = () => loadOverride(entry.id, session);
        await updatePlanningInThePastOverrideRequest(entry.id, {
          rowVersion: entry.rowVersion,
          apiScopes: selectedScopes,
          workerClassStates,
          ...toPayload(),
        });
      }
      if (session === sessionRef.current) reset();
      callbacksRef.current.onSaved(savedMessageKey);
    } catch (error) {
      const parsed = parseContractComplianceError(error);
      const message = getContractComplianceErrorMessage(t, parsed, GENERAL__ERROR_MESSAGE);
      // Nothing was saved: the panel keeps the values and shows the error at its top. Not the bottom-right banner:
      // in a side panel it covers the Save button.
      if (!handleWriteError(parsed, message, session, reloadEntry)) setSaveErrorMessage(message);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    states,
    mode,
    role,
    entry,
    selectedApiUserId,
    selectedScopes,
    toPayload,
    reset,
    loadRole,
    loadOverride,
    handleWriteError,
    t,
  ]);

  const requestRemove = useCallback(() => {
    if (mode === 'edit' && status === 'ready' && !savingRef.current) setIsRemoveOpen(true);
  }, [mode, status]);

  const cancelRemove = useCallback(() => setIsRemoveOpen(false), []);

  // The retry closure must call the latest runRemove, not the one captured when the banner was shown.
  const runRemoveRef = useRef<
    (target: IPlanningInThePastEntry, payload: IRemoveRuleException, session: number) => Promise<void>
  >(async () => undefined);

  /**
   * Shared by the disclaimer's Confirm and the Retry in the failure banner. The retry can run after the panel closed,
   * so the target and payload are passed in, and the same double-submit guard applies.
   */
  const runRemove = useCallback(
    async (target: IPlanningInThePastEntry, payload: IRemoveRuleException, session: number) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
      setIsRemoveOpen(false);
      setSaveErrorMessage(null);
      try {
        await removePlanningInThePastOverrideRequest(target.id, payload);
        if (session === sessionRef.current) reset();
        callbacksRef.current.onSaved(CONTRACT_COMPLIANCE__PLANNING_OVERRIDE_REMOVED);
      } catch (error) {
        const parsed = parseContractComplianceError(error);
        const message = getContractComplianceErrorMessage(t, parsed, GENERAL__ERROR_MESSAGE);
        if (!handleWriteError(parsed, message, session, () => loadOverride(target.id, session))) {
          // The override stays in place; the bottom-right banner offers a retry.
          callbacksRef.current.onRemoveFailed(message, () => {
            runRemoveRef.current(target, payload, session);
          });
        }
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [reset, handleWriteError, loadOverride, t],
  );
  runRemoveRef.current = runRemove;

  const confirmRemove = useCallback(() => {
    if (!entry) return;
    // Captured now: the retry from the banner may run after the panel has closed.
    runRemove(entry, { rowVersion: entry.rowVersion, ...toPayload() }, sessionRef.current);
  }, [entry, toPayload, runRemove]);

  const close = useCallback(() => {
    reset();
    callbacksRef.current.onClosed();
  }, [reset]);

  return {
    mode,
    role,
    status,
    entry,
    eligibleUsers,
    states,
    setWorkerClassState,
    selectedApiUserId,
    selectApiUser,
    selectedScopes,
    selectScopes,
    availableScopes,
    isComplete,
    isSaving,
    isConfirmOpen,
    isRemoveOpen,
    loadErrorMessage,
    saveErrorMessage,
    justification,
    openRole,
    openAdd,
    openEdit,
    retryLoad,
    requestSave,
    cancelConfirm,
    confirmSave,
    requestRemove,
    cancelRemove,
    confirmRemove,
    close,
  };
};

export type UsePlanningInThePastPanelResult = ReturnType<typeof usePlanningInThePastPanel>;
